"use client"

import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import * as React from "react"

import { Button } from "../Button/button"
import * as dialogStyles from "./dialog.styled"

const Dialog = DialogPrimitive.Root

const DialogPrimitiveContent = DialogPrimitive.Content

const DialogTrigger = ({ ...props }: DialogPrimitive.DialogTriggerProps) => (
  <DialogPrimitive.Trigger {...props} asChild />
)
DialogTrigger.displayName = DialogPrimitive.Trigger.displayName

const DialogClose = ({ ...props }: DialogPrimitive.DialogCloseProps) => (
  <DialogPrimitive.Close {...props} asChild />
)
DialogClose.displayName = DialogPrimitive.Close.displayName

const DialogPortal = ({ ...props }: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props} />
)
DialogPortal.displayName = DialogPrimitive.Portal.displayName

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={dialogStyles.dialogOverlayStyle(className)}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitiveContent
      ref={ref}
      className={dialogStyles.dialogContentStyle(className)}
      {...props}
    >
      <DialogClose className={dialogStyles.dialogCloseStyle}>
        <Button shape="circle" variant="tertiary" size="sm">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </Button>
      </DialogClose>
      {children}
    </DialogPrimitiveContent>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={dialogStyles.dialogFooterStyle(className)} {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogIcon = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={dialogStyles.dialogIconStyle(className)} {...props} />
)
DialogIcon.displayName = "DialogIcon"

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={dialogStyles.dialogTitleStyle(className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={dialogStyles.dialogDescriptionStyle(className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

const DialogTextContainer = ({
  children,
}: React.PropsWithChildren<React.ComponentProps<"div">>) => (
  <div className={dialogStyles.dialogTextContainer}>{children}</div>
)

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogTextContainer,
  DialogIcon,
  DialogClose,
  DialogPortal,
  DialogOverlay,
  DialogPrimitiveContent,
}
